import React, { useCallback, useState, useEffect, useRef, ReactElement } from 'react';
import { css } from '@emotion/react';
import { useBannerCarouselContext } from './banner-carousel-context';

interface BannerCarouselSlideProps {
  name: string;
  children: ReactElement<{ innerRef?: React.LegacyRef<HTMLElement> }> | null | false;
}

export const BannerCarouselSlide: React.FC<BannerCarouselSlideProps> = ({ name, children }) => {
  const { registerSlide, unregisterSlide, activeSlides, setSlideHeight } = useBannerCarouselContext();
  const [hasContent, setHasContent] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);

  const handleInnerRef = useCallback(
    (node: HTMLElement | null) => {
      elementRef.current = node;
      if (node) {
        setHasContent(true);
        registerSlide(name);
        setSlideHeight(name, node.offsetHeight);
      } else {
        setHasContent(false);
        unregisterSlide(name);
      }
    },
    [name, registerSlide, unregisterSlide, setSlideHeight]
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        setSlideHeight(name, height);
      }
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [name, setSlideHeight, hasContent]);

  const isActive = activeSlides.includes(name);

  // Inject innerRef into the child so we can detect its mount state
  const renderedChild = React.isValidElement(children)
    ? React.cloneElement(children, {
        innerRef: handleInnerRef,
      })
    : children;

  return (
    <div
      css={
        !hasContent || !isActive
          ? css`
              display: none;
            `
          : undefined
      }
    >
      {renderedChild}
    </div>
  );
};
